'use client'

import { datadogLogs } from '@datadog/browser-logs'
import type { LogsInitConfiguration } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import type { RumInitConfiguration } from '@datadog/browser-rum'
import { useEffect } from 'react'

import type { InitContext } from '@src/app/init'
import { configPublic } from '@src/config.public'
import { useSession } from '@src/hooks/useSession'
import { log } from '@src/utils/log.util'

import { getBuilderPreviewMode } from './builder.util'

const {
  allowedTracingUrls,
  applicationId,
  clientToken,
  env,
  isEnabled,
  sessionReplaySampleRate,
  sessionSampleRate,
  site,
  service,
} = configPublic.datadog

const { appVersion, commitSHA } = configPublic.general
const version = commitSHA ? `${appVersion}-${commitSHA}` : appVersion

export function useDataDog({ vercelDeploymentUrl }: InitContext) {
  const { edit } = getBuilderPreviewMode()
  const { commercetoolsSession, anonymousId } = useSession()

  useEffect(() => {
    if (edit) return

    const configRUM: RumInitConfiguration = {
      applicationId,
      clientToken,
      site: site as RumInitConfiguration['site'],
      service,
      env,
      version,
      sessionSampleRate,
      sessionReplaySampleRate,
      allowedTracingUrls,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      silentMultipleInit: true,
    }

    const configLogs: LogsInitConfiguration = {
      clientToken,
      site: site as LogsInitConfiguration['site'],
      sessionSampleRate,
      service,
      env,
      version,
      forwardConsoleLogs: [],
      forwardReports: 'all',
      forwardErrorsToLogs: true,
      silentMultipleInit: true,
    }

    log.debug(`[DATADOG] Configs:`, { configRUM, configLogs })
    if (!isEnabled) return

    // Initialize DataDog RUM
    datadogRum.init(configRUM)

    // Initialize DataDog logging
    datadogLogs.init({
      ...configLogs,
      beforeSend: (event, _context) => {
        // transform ad-blockers errors to warnings
        if (
          event.message.includes('Amplitude Logger [Error]') ||
          event.message.includes('Fetch error POST https://api2.amplitude.com/2/httpapi')
        ) {
          event.status = 'warn'
          event.message = '🚫 Amplitude Logger blocked by ad-blocker extension'
        }
        if (event.message.includes('Fetch error POST https://cdn.builder.io/api/v1/track')) {
          event.status = 'warn'
          event.message = '🚫 Builder Logger blocked by ad-blocker extension'
        }
        return true
      },
    })

    // Initialize DataDog RUM Session Replay Recordings
    datadogRum.startSessionReplayRecording()
  }, [edit])

  // Set global context properties
  useEffect(() => {
    if (edit) return

    datadogLogs.setGlobalContextProperty('vercelDeploymentUrl', vercelDeploymentUrl)

    if (anonymousId) {
      datadogRum.setUser({ id: anonymousId })
      datadogLogs.setGlobalContextProperty('sessionId', anonymousId)
    }
    if (commercetoolsSession) {
      datadogLogs.setGlobalContextProperty(
        'cartId',
        commercetoolsSession[commercetoolsSession?.country]?.cartId
      )
    }
  }, [edit, commercetoolsSession, anonymousId, vercelDeploymentUrl])
}
